import { svg, esc } from '../ui/icons.js';
import * as Indicadores from '../data/indicadores.js';

/**
 * Painel de retrabalho e correções.
 *
 * Responde ao que a Beatriz pediu: o que gerou retrabalho, o que precisou
 * ser refeito e onde os problemas se concentram.
 *
 * O que NÃO responde, e é bom deixar explícito na tela: "qual o tipo de
 * erro mais comum". A correção é texto livre, sem categoria — decisão dela.
 * Dá para dizer quanto, de quem, em qual empresa e em qual frente.
 */

let periodo = { de: null, ate: null };

/*
 * Autocorreção fica de fora por padrão.
 *
 * Ela preencheu a SK, que é dela, corrigiu a própria marcação enquanto
 * preenchia, e o painel acusou 24 retrabalhos. Ninguém refez trabalho de
 * ninguém ali. A chave existe para quando ela quiser ver o movimento
 * bruto, mas a leitura normal é sem.
 */
let incluirAutocorrecao = false;

export function configurar() {
  document.getElementById('indAutocorrecao')?.addEventListener('change', (ev) => {
    incluirAutocorrecao = ev.target.checked;
    render();
  });

  document.getElementById('indPeriodo')?.addEventListener('change', (ev) => {
    const meses = Number(ev.target.value);
    if (!meses) {
      periodo = { de: null, ate: null };
    } else {
      const d = new Date();
      d.setMonth(d.getMonth() - meses);
      periodo = { de: d.toISOString().slice(0, 10), ate: null };
    }
    render();
  });
}

export async function render() {
  const alvo = document.getElementById('indWrap');
  if (!alvo) return;

  alvo.innerHTML = `<p class="ind-carregando">Calculando indicadores…</p>`;

  try {
    // Tempo de fechamento ficou de fora a pedido da Beatriz. A função
    // painel_tempo_fechamento() continua no banco (migration 0007) para
    // quando fizer sentido — só não é chamada aqui.
    const [rt, cor] = await Promise.all([
      Indicadores.retrabalho({ ...periodo, incluirAutocorrecao }),
      Indicadores.correcoes(periodo),
    ]);

    alvo.innerHTML = `
      ${cartoes(rt, cor)}

      <div class="ind-colunas">
        ${ranking('Onde o trabalho mais volta', 'Empresas com mais itens que saíram de OK', rt.por_empresa, rt.total, { o: 'retrabalho', campo: 'empresa' })}
        ${ranking('Qual frente mais volta', 'Docs, Folha, Fiscal, Contábil ou Conciliação', rt.por_item, rt.total, { o: 'retrabalho', campo: 'item' })}
        ${ranking('Qual etapa do checklist mais trava', 'Etapas desmarcadas depois de feitas', rt.por_etapa, rt.total, { o: 'retrabalho', campo: 'item' })}
        ${ranking('Correções por empresa', 'Apontadas na revisão', cor.por_empresa, cor.correcoes, { o: 'correcoes', campo: 'empresa' })}
        ${ranking('Correções por responsável', 'Quem recebeu a orientação', cor.por_responsavel, cor.correcoes)}
        ${ranking('Retrabalho por colaborador', 'Quem desfez — não necessariamente quem errou', rt.por_colaborador, rt.total, { o: 'retrabalho', campo: 'colaborador_nome' })}
      </div>

      ${serie(rt.por_mes, cor.por_mes)}

      <div class="section ind-casos" id="indCasos" hidden></div>
      <p class="ind-nota">
        ${svg('alert', 13)}
        <span><b>O que este painel não responde:</b> qual o tipo de erro mais comum.
        A correção é registrada como texto livre, sem categoria — então dá para
        saber <i>quanto</i>, <i>de quem</i>, <i>em qual empresa</i> e <i>em qual frente</i>,
        mas não classificar o erro. Se quiser esse corte, é só acrescentar
        uma categoria no comentário.</span>
      </p>`;

    ligarCasos(alvo);
  } catch (err) {
    alvo.innerHTML = `<p class="ind-erro">Não foi possível calcular: ${esc(err.message)}</p>`;
  }
}

/* ------------------------------------------------------------------ */

function cartoes(rt, cor) {
  const itens = [
    // Sem dizer quantas autocorreções ficaram de fora, o zero parece
    // erro — ela sabe que mexeu na SK e esperaria ver alguma coisa.
    { rotulo: 'RETRABALHO NO PERÍODO', num: rt.total,
      sub: `${rt.empresas_afetadas || 0} empresas afetadas`
        + (!incluirAutocorrecao && rt.autocorrecoes
            ? ` · ${rt.autocorrecoes} autocorreção(ões) fora da conta` : ''),
      cor: rt.total ? 'red' : 'green',
      abrir: { o: 'retrabalho', titulo: 'Retrabalho no período' } },
    { rotulo: 'CORREÇÕES APONTADAS', num: cor.correcoes,
      sub: `${cor.correcoes_em_aberto ?? 0} ainda em aberto`,
      cor: cor.correcoes_em_aberto ? 'amber' : 'green',
      abrir: { o: 'correcoes', tipo: 'correcao', titulo: 'Correções apontadas' } },
    { rotulo: 'ORIENTAÇÕES E DÚVIDAS', num: (cor.orientacoes || 0) + (cor.duvidas || 0),
      sub: `${cor.orientacoes || 0} orientações · ${cor.duvidas || 0} dúvidas`, cor: 'purple',
      abrir: { o: 'correcoes', titulo: 'Orientações e dúvidas' } },
    { rotulo: 'CORREÇÕES RESOLVIDAS', num: Math.max(0, (cor.correcoes || 0) - (cor.correcoes_em_aberto || 0)),
      sub: 'já tratadas pelo responsável', cor: 'green',
      abrir: { o: 'correcoes', tipo: 'correcao', resolvido: true, titulo: 'Correções já resolvidas' } },
  ];

  return `<div class="ind-cartoes">${itens.map((i) => `
    <div class="ind-cartao ${i.cor} ${i.num ? 'acionavel' : ''}"
         ${i.num ? `data-abrir="${esc(JSON.stringify(i.abrir))}"` : ''}>
      <p class="rotulo">${i.rotulo}</p>
      <p class="num">${i.num}</p>
      <p class="sub">${esc(i.sub)}${i.num ? ' · clique para ver' : ''}</p>
    </div>`).join('')}</div>`;
}

function ranking(titulo, subtitulo, linhas, total, abrir = null) {
  const maior = Math.max(1, ...(linhas || []).map((l) => l.n));

  return `
    <div class="ind-bloco">
      <h3>${esc(titulo)}</h3>
      <p class="ind-sub">${esc(subtitulo)}</p>
      ${(linhas || []).length ? `
        <div class="ind-barras">
          ${linhas.map((l) => `
            <div class="ind-barra ${abrir ? 'acionavel' : ''}"
                 ${abrir ? `data-abrir="${esc(JSON.stringify({ ...abrir, valor: l.nome, titulo: `${titulo} · ${l.nome}` }))}"` : ''}>
              <span class="nome" title="${esc(l.nome)}">${esc(l.nome)}</span>
              <span class="trilho"><span class="preenche" style="width:${(l.n / maior) * 100}%;"></span></span>
              <span class="valor">${l.n}</span>
            </div>`).join('')}
        </div>
        ${total ? `<p class="ind-rodape">${linhas.reduce((s, l) => s + l.n, 0)} de ${total} no período</p>` : ''}
      ` : `<p class="ind-vazio">Nada registrado no período.</p>`}
    </div>`;
}

function serie(retrabalho, correcoes) {
  const meses = [...new Set([
    ...(retrabalho || []).map((x) => x.mes),
    ...(correcoes || []).map((x) => x.mes),
  ])].sort();

  if (!meses.length) {
    return `<div class="ind-bloco"><h3>Evolução</h3>
      <p class="ind-vazio">Ainda não há meses com registro suficiente para comparar.</p></div>`;
  }

  const val = (lista, mes) => (lista || []).find((x) => x.mes === mes)?.n || 0;
  const maior = Math.max(1, ...meses.map((m) => Math.max(val(retrabalho, m), val(correcoes, m))));
  const nomeMes = (m) => {
    const [a, mm] = m.split('-');
    return ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'][+mm - 1] + '/' + a.slice(2);
  };

  return `
    <div class="ind-bloco ind-largo">
      <h3>Evolução no tempo</h3>
      <p class="ind-sub">Pelo mês em que aconteceu, não pela competência — mostra se está melhorando</p>
      <div class="ind-serie">
        ${meses.map((m) => `
          <div class="ind-coluna">
            <div class="ind-pilha">
              <span class="p-retrabalho" style="height:${(val(retrabalho, m) / maior) * 100}%;" title="Retrabalho: ${val(retrabalho, m)}"></span>
              <span class="p-correcao" style="height:${(val(correcoes, m) / maior) * 100}%;" title="Correções: ${val(correcoes, m)}"></span>
            </div>
            <span class="ind-mes">${nomeMes(m)}</span>
          </div>`).join('')}
      </div>
      <p class="ind-legenda">
        <span><i class="c-retrabalho"></i> Retrabalho</span>
        <span><i class="c-correcao"></i> Correções</span>
      </p>
    </div>`;
}


/* ------------------------------------------------------------------ */
/* Os casos por trás do número                                         */
/*                                                                     */
/* Pergunta dela: "esses painéis são utilizáveis ou só demonstrativo?" */
/* Eram só demonstrativo — o número não levava a lugar nenhum. Saber    */
/* que houve 3 retrabalhos não ajuda; ver que os 3 foram na mesma       */
/* frente da mesma empresa, no mesmo mês, ajuda.                       */
/* ------------------------------------------------------------------ */

const dataHora = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.toLocaleDateString('pt-BR')} ${d.toTimeString().slice(0, 5)}`;
};

const mesAno = (d) => (d
  ? new Date(d + 'T00:00').toLocaleDateString('pt-BR', { month: '2-digit', year: 'numeric' })
  : '—');

function ligarCasos(raiz) {
  raiz.querySelectorAll('[data-abrir]').forEach((el) =>
    el.addEventListener('click', () => abrirCasos(JSON.parse(el.dataset.abrir)))
  );
}

async function abrirCasos(alvo) {
  const painel = document.getElementById('indCasos');
  painel.hidden = false;
  painel.innerHTML = `<p class="ind-carregando">Buscando os casos…</p>`;
  painel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

  // O ranking manda o campo pelo qual filtrar; o cartão não filtra nada.
  const filtro = { ...periodo, incluirAutocorrecao };
  if (alvo.campo === 'empresa') filtro.empresa = alvo.valor;
  if (alvo.campo === 'item') filtro.item = alvo.valor;
  if (alvo.tipo) filtro.tipo = alvo.tipo;
  if (alvo.resolvido !== undefined) filtro.resolvido = alvo.resolvido;

  try {
    let casos = alvo.o === 'retrabalho'
      ? await Indicadores.casosRetrabalho(filtro)
      : await Indicadores.casosCorrecoes(filtro);

    /*
     * O ranking por colaborador traz o nome; a função do banco filtra por
     * id. Em vez de mapear nome → id na tela (dois colaboradores podem se
     * chamar igual amanhã), peneiro aqui — são no máximo 200 linhas.
     */
    if (alvo.campo === 'colaborador_nome') {
      casos = casos.filter((c) => (c.colaborador || c.autor) === alvo.valor);
    }

    const colunas = alvo.o === 'retrabalho'
      ? ['Empresa', 'Competência', 'O que voltou', 'Mudança', 'Quem desfez', 'Quando']
      : ['Empresa', 'Competência', 'Item', 'Tipo', 'O que foi apontado', 'Quem', 'Situação'];

    const linha = (c) => alvo.o === 'retrabalho'
      ? `<tr>
           <td>${esc(c.empresa)}</td><td>${mesAno(c.competencia)}</td>
           <td>${esc(c.item)}</td>
           <td><span class="tag red">${esc(c.status_anterior)} → ${esc(c.status_novo)}</span></td>
           <td>${esc(c.colaborador)}</td><td>${dataHora(c.quando)}</td>
         </tr>`
      : `<tr>
           <td>${esc(c.empresa)}</td><td>${mesAno(c.competencia)}</td>
           <td>${esc(c.item)}</td><td>${esc(c.tipo)}</td>
           <td style="max-width:320px;">${esc(c.texto)}</td>
           <td>${esc(c.autor)}</td>
           <td><span class="tag ${c.resolvido ? 'green' : 'amber'}">${c.resolvido ? 'Resolvida' : 'Em aberto'}</span></td>
         </tr>`;

    painel.innerHTML = `
      <div class="ind-casos-topo">
        <div>
          <h3>${esc(alvo.titulo || 'Casos')}</h3>
          <p class="ind-sub">${casos.length} caso(s)${casos.length >= 200 ? ' — mostrando os 200 mais recentes' : ''}</p>
        </div>
        <button type="button" class="kb-fechar" id="indFecharCasos">Fechar</button>
      </div>
      ${casos.length
        ? `<div class="table-wrap"><table>
             <thead><tr>${colunas.map((c) => `<th>${c}</th>`).join('')}</tr></thead>
             <tbody>${casos.map(linha).join('')}</tbody>
           </table></div>`
        : `<p class="ind-vazio">Nenhum caso com esse recorte.</p>`}`;

    document.getElementById('indFecharCasos').addEventListener('click', () => {
      painel.hidden = true;
      painel.innerHTML = '';
    });
  } catch (erro) {
    painel.innerHTML = `<p class="ind-vazio">Não consegui buscar os casos: ${esc(erro.message)}</p>`;
  }
}
